import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Alert, Avatar, Button, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import {
  BookOutlined, CalendarOutlined, DollarOutlined, EditOutlined, FileTextOutlined, TeamOutlined,
} from '@ant-design/icons';
import { productApi } from '../../api';

/**
 * Teacher — Profile. Shows the teacher's name/role/phone/email + the weekly
 * timetable grouped by day.
 */
const DAY_LABELS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function TeacherProfilePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['teacher-profile', institution],
    queryFn: () => productApi.getTeacherProfile(institution),
    enabled: !!institution,
  });

  const byDay = useMemo(() => {
    const map: Record<number, typeof q.data extends { weekly_slots: infer T } ? T : never> = {} as never;
    for (const s of q.data?.weekly_slots ?? []) {
      const key = s.day_of_week;
      const arr = ((map as unknown as Record<number, typeof s[]>)[key] ??= []);
      arr.push(s);
    }
    return map as unknown as Record<number, Array<{ day_of_week: number; period: number; grade: string; section: string; subject_name: string }>>;
  }, [q.data]);

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Could not load profile" />;

  if (!q.data.teacher) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Your login email isn't linked to a staff record here"
        description="Ask the admin to add you as staff with the same email."
      />
    );
  }

  const t = q.data.teacher;
  // Distinct sections I teach — for quick-open roster links.
  const mySections: Array<{ grade: string; section: string }> = Array.from(
    new Map(
      (q.data.weekly_slots ?? []).map((s) => [`${s.grade}|${s.section}`, { grade: s.grade, section: s.section }]),
    ).values(),
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space size={12} align="center">
          <Avatar size={54} style={{ background: '#2f6ab0', fontSize: 18 }}>
            {t.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{t.name}</div>
            <div style={{ color: '#5b6478', fontSize: 12 }}>{t.role.replace(/_/g, ' ')}</div>
            {t.email && <div style={{ color: '#5b6478', fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{t.email}</div>}
            <div style={{ color: '#5b6478', fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{t.phone}</div>
          </div>
        </Space>
      </Card>

      <Card size="small" title="Quick links">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          <Link to={`/i/${institution}/teacher/timetable`}>
            <Button block icon={<CalendarOutlined />}>My week</Button>
          </Link>
          <Link to={`/i/${institution}/teacher/payslips`}>
            <Button block icon={<DollarOutlined />}>Payslips</Button>
          </Link>
          <Link to={`/i/${institution}/teacher/requests`}>
            <Button block icon={<FileTextOutlined />}>Requests</Button>
          </Link>
          <Link to={`/i/${institution}/teacher/gradebook`}>
            <Button block icon={<BookOutlined />}>Gradebook</Button>
          </Link>
          <Link to={`/i/${institution}/teacher/diary/new`}>
            <Button block icon={<EditOutlined />}>Diary note</Button>
          </Link>
        </div>
        {mySections.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#8a94a6', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              <TeamOutlined /> My sections
            </div>
            <Space wrap size={6}>
              {mySections.map((s) => (
                <Link key={`${s.grade}-${s.section}`} to={`/i/${institution}/teacher/class/${encodeURIComponent(s.grade)}/${encodeURIComponent(s.section)}`}>
                  <Tag color="blue" style={{ cursor: 'pointer', margin: 0 }}>
                    {s.grade}{s.section ? `-${s.section}` : ''}
                  </Tag>
                </Link>
              ))}
            </Space>
          </div>
        )}
      </Card>

      <Card size="small" title={`Weekly load · ${q.data.slot_count} period${q.data.slot_count === 1 ? '' : 's'}`}>
        {q.data.slot_count === 0 ? (
          <Empty description="No periods assigned yet." style={{ padding: 20 }} />
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {[1, 2, 3, 4, 5, 6].map((d) => {
              const slots = byDay[d] ?? [];
              if (slots.length === 0) return null;
              return (
                <div key={d}>
                  <Typography.Text style={{ fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase', color: '#8a94a6', fontWeight: 600 }}>
                    {DAY_LABELS[d]} · {slots.length}
                  </Typography.Text>
                  <div style={{ marginTop: 6 }}>
                    <Space wrap size={6}>
                      {slots.sort((a, b) => a.period - b.period).map((s) => (
                        <Tag key={`${s.day_of_week}-${s.period}`} color="processing">
                          P{s.period} · {s.subject_name} · {s.grade}{s.section ? `-${s.section}` : ''}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </div>
              );
            })}
          </Space>
        )}
      </Card>
    </Space>
  );
}
